import { useToast } from "primevue/usetoast";
import i18n from "@/i18n";

interface ApiResponseError {
  message: string;
  data?: any;
  [key: string]: any;
}

class NotifyService {
  private toast: ReturnType<typeof useToast> | null = null;

  /**
   * Postavlja instancu toasta koju će koristiti ostale metode.
   * @param toast PrimeVue Toast Service
   */
  setToastInstance(toast: ReturnType<typeof useToast>) {
    this.toast = toast;
  }

  // Funkcija koja rekurzivno prolazi kroz objekt greške i vraća sve poruke
  private extractErrorMessages(
    errorData: any,
    parentKey: string = ""
  ): string[] {
    const messages: string[] = [];

    if (Array.isArray(errorData)) {
      // Ako je poruka niz (lista poruka), dodaj sve elemente u niz
      errorData.forEach((msg) => {
        messages.push(`${parentKey}: ${msg}`);
      });
    } else if (typeof errorData === "object" && errorData !== null) {
      // Ako je objekt, rekurzivno prolazi kroz njega
      for (const key in errorData) {
        if (errorData.hasOwnProperty(key)) {
          const newKey = parentKey ? `${parentKey} -> ${key}` : key;
          messages.push(...this.extractErrorMessages(errorData[key], newKey));
        }
      }
    } else if (typeof errorData === "string") {
      // Ako je string, dodaj ga u niz sa ključem
      messages.push(`${parentKey}: ${errorData}`);
    }

    return messages;
  }
  private localizeErrorMessage(errorCode: string): string {
    return (
      i18n.global.t(`validation.${errorCode}`) ||
      i18n.global.t("validation.unknownError")
    );
  }

  notifyUserAboutApiError(errorResponse: ApiResponseError): void {
    const mainMessageCode = errorResponse?.message || "unknownError";
    const errorDetails = errorResponse?.data || {};

    // Lokalizovana glavna poruka greške
    const mainMessage = this.localizeErrorMessage(mainMessageCode);

    // Dobijanje svih poruka o greškama rekurzivno
    const errorMessages = this.extractErrorMessages(errorDetails);

    if (this.toast) {
      this.toast.add({
        severity: "error",
        summary: i18n.global.t("validation.error"),
        detail: mainMessage,
        life: 4000,
      });

      // Prikaz svih detaljnih poruka greške
      errorMessages.forEach((message) => {
        this.toast?.add({
          severity: "error",
          summary: i18n.global.t("validation.errorDetails"),
          detail: message,
          life: 5000,
        });
      });
    }
  }

  // Notifikacija korisnika o uspešnoj akciji
  notifyUserAboutApiSuccess(message: string): void {
    if (this.toast) {
      this.toast.add({
        severity: "success",
        summary: "Success",
        detail: message,
        life: 3000,
      });
    }
  }
}

export const notifyService = new NotifyService();
